import { useMutation } from '@tanstack/react-query';
import { Navigate, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { KeyRound, Loader2, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { authApi } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { PasswordFields, type PasswordFormValues } from './PasswordFields';
import { EMPTY_PASSWORD_FORM, passwordFormSchema } from './passwordForm';
import { useAuthStore } from './store';

/** Troca de senha obrigatória (primeiro acesso ou senha redefinida por um administrador). */
export function ChangePasswordPage() {
  const { user, setUser, logout } = useAuthStore();
  const navigate = useNavigate();
  const form = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordFormSchema),
    defaultValues: EMPTY_PASSWORD_FORM,
  });

  const mut = useMutation({
    mutationFn: (v: PasswordFormValues) =>
      authApi.changePassword({ currentPassword: v.currentPassword, newPassword: v.newPassword }),
    onSuccess: (u) => {
      setUser(u);
      toast.success('Senha definida. Bem-vindo!');
      navigate('/dashboard', { replace: true });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Erro ao trocar a senha'),
  });

  if (user && !user.mustChangePassword) return <Navigate to="/dashboard" replace />;

  const handleLogout = async () => {
    await authApi.logout().catch(() => undefined);
    logout();
    navigate('/login');
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background via-background to-primary/10 p-4">
      <Card className="w-full max-w-sm shadow-xl">
        <CardHeader className="space-y-3 text-center">
          <div className="mx-auto flex size-12 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <KeyRound className="size-6" />
          </div>
          <CardTitle className="text-xl">Defina uma nova senha</CardTitle>
          <CardDescription>
            {user?.name ? `Olá, ${user.name.split(' ')[0]}. ` : ''}
            Por segurança, troque a senha temporária antes de continuar.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={form.handleSubmit((v) => mut.mutate(v))} className="space-y-4" noValidate>
            <PasswordFields form={form} idPrefix="first" />
            <Button type="submit" className="w-full" disabled={mut.isPending}>
              {mut.isPending && <Loader2 className="animate-spin" />}
              Salvar e continuar
            </Button>
            <Button type="button" variant="ghost" className="w-full" onClick={handleLogout}>
              <LogOut /> Sair
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
